import dayjs from 'dayjs'

/**
 * Formats a date using the specified pattern.
 * @param {Date | string | number} date - The date to format.
 * @param {string} [pattern='DD/MM HH:mm:ss'] - The pattern to format the date.
 * @returns {string} The formatted date string.
 */
export const formatDate = (date, pattern = 'DD/MM HH:mm:ss') => (date ? dayjs(date).format(pattern) : '')

/**
 * Counts the number of decimal places in a string representation of a number.
 * @param {string} str - The string representation of the number.
 * @returns {number} The number of decimal places.
 */
function countDecimalPlaces(str) {
  const decimalIndex = str.indexOf('.')
  return decimalIndex === -1 ? 0 : str.length - decimalIndex - 1
}

/**
 * Formats a number with commas and specified decimal places.
 * @param {number | string} num - The number to format.
 * @param {number} [decimals] - The number of decimal places to include.
 * @returns {string} The formatted number as a string with commas.
 */
export function formatNumber(num, decimals) {
  if (!num) return num
  num = num.toString()

  // Automatically calculate decimal places if not provided
  if (decimals === null || decimals === undefined) {
    decimals = countDecimalPlaces(num)
  } else if (!decimals) {
    decimals = 0
  }
  const [integerNum, decimalNum] = Number(num).toFixed(decimals).split('.')
  const formattedIntegerNum = integerNum.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  return formattedIntegerNum + (decimalNum ? '.' + decimalNum : '')
}

/**
 * Rounds a number to the specified precision.
 * @param {number} number - The number to round.
 * @param {number} precision - The precision to round to.
 * @returns {number} The rounded number.
 */
export function round(number, precision) {
  return Math.round(Number(number + 'e' + precision)) / Math.pow(10, precision)
}

/**
 * Formats a number to two decimal places and adds commas.
 * @param {number} value - The value to format.
 * @returns {string} The formatted number.
 */
export const formatRoundNumber = (value) => {
  // 返回处理后的值
  if (!value) return value
  return formatNumber(round(value, 2), 2)
}

/**
 * Formats a monetary value by dividing by 10000 and adding commas.
 * @param {number} value - The value to format.
 * @param {boolean} [keepZero=false] - Whether to keep zero values as '0.00'.
 * @returns {string} The formatted monetary value.
 */
export const formatMoney = (value, keepZero = false) => {
  if (value === null || value === undefined) return keepZero ? '0.00' : ''
  return formatNumber(value / 10000, 2)
}

/**
 * Converts a string to camelCase.
 * @param {string} str - The string to convert.
 * @param {string} [devider=' '] - The delimiter to use for splitting the string.
 * @returns {string} The camelCase string.
 */
export const transformCamelCase = (str = '', devider = ' ') => {
  if (/^[A-Z]/.test(str)) {
    str = str.replace(/^([A-Z])/, ($0) => $0.toLowerCase())
  }
  const reg = new RegExp(`${devider}(\\w)`, 'g')
  return str.replace(reg, (_$0, $1) => $1.toUpperCase())
}

/**
 * Formats a phone number by masking the middle digits.
 * @param {string} phone - The phone number to format.
 * @returns {string} The formatted phone number.
 */
export function formatPhone(phone) {
  if (phone && phone.length > 6) {
    return phone.substr(0, 3) + '*'.repeat(phone.length - 6) + phone.substr(-3)
  } else {
    return ''
  }
}

/**
 * Replaces a string value of '0.00' with an empty string.
 * @param {string | number} value - The value to check.
 * @returns {string | number} The original value or an empty string if the value is '0.00'.
 */
export const replaceZeroValue = (value) => {
  return value === '0.00' ? '' : value
}
